import { BarListCard } from '@/components/cards/bar-list-card';
import { RecentSalesCard } from '@/components/cards/recent-sales-card';
import { SalesCard } from '@/components/cards/sales-card';
import { SnapshotCard } from '@/components/cards/snapshot-card';
import { Layout } from '@/components/layouts/layout';
import { Button } from '@/components/ui/button';
import {
    ChartContainer,
    ChartLegend,
    ChartLegendContent,
    ChartTooltip,
    ChartTooltipContent,
} from '@/components/ui/chart';
import {
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useInventoryAll } from '@/hooks/use-inventory';
import {
    averageBy,
    buildMarginDrivers,
    buildMonthlyCashFlow,
    buildMonthlySellThrough,
    buildWeeklySellThrough,
    countBy,
    normalizeValue,
    roiByGroup,
    sumBy,
    sumByGroup,
} from '@/lib/dashboard-metrics';
import {
    formatCurrency,
    formatPercent,
    formatRelativeDate,
    getDaysSince,
    parseDate,
} from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { InventoryRecord } from '@/types/inventory';
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangle, Clock, Package, Plus } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

export const Route = createFileRoute('/analytics')({
    component: AnalyticsPage,
});

function AnalyticsPage() {
    const { data, isLoading, isError, error } = useInventoryAll();
    const rows = data?.list ?? [];

    const soldItems = rows.filter(
        (item) => normalizeValue(item['Item Status']) === 'sold',
    );
    const listedItems = rows.filter(
        (item) => normalizeValue(item['Item Status']) === 'listed',
    );

    function getNumber(value: unknown) {
        return typeof value === 'number' && Number.isFinite(value) ? value : 0;
    }

    function getSoldFees(item: InventoryRecord) {
        return getNumber(item['Platform Fees']) || 0;
    }

    function getNetProfit(item: InventoryRecord) {
        const precomputed = item['Net Profit'];
        if (typeof precomputed === 'number' && Number.isFinite(precomputed)) {
            return precomputed;
        }
        return (
            getNumber(item['Sale Price']) -
            getNumber(item['Cost']) -
            getSoldFees(item)
        );
    }

    const soldNetProfit = sumBy(soldItems, (item) => getNetProfit(item));
    const soldSales = sumBy(soldItems, (item) => getNumber(item['Sale Price']));
    const soldCost = sumBy(soldItems, (item) => getNumber(item['Cost']));
    const soldFees = sumBy(soldItems, (item) => getSoldFees(item));
    const totalInventoryCost = sumBy(rows, (item) => getNumber(item['Cost']));
    const roi = soldCost > 0 ? (soldNetProfit / soldCost) * 100 : null;
    const avgDaysToSell = averageBy(soldItems, (item) => item['Days to Sell']);
    const sellThroughRate =
        rows.length > 0 ? (soldItems.length / rows.length) * 100 : 0;
    const businessCashPosition = soldSales - totalInventoryCost - soldFees;

    const soldPct =
        rows.length > 0 ? (soldItems.length / rows.length) * 100 : 0;
    const listedPct =
        rows.length > 0 ? (listedItems.length / rows.length) * 100 : 0;
    const otherPct = Math.max(0, 100 - soldPct - listedPct);
    const staleCount = listedItems.filter((item) => {
        const days = getDaysSince(item['Date Listed']);
        return days !== null && days >= 45;
    }).length;

    const categoryCounts = countBy(rows, (item) => item.Category || 'Unknown');
    const statusCounts = countBy(
        rows,
        (item) => item['Item Status'] || 'Unknown',
    );
    const platformCounts = countBy(
        soldItems,
        (item) => item['Sold Platform'] || 'Unknown',
    );
    const platformProfit = sumByGroup(
        soldItems,
        (item) => item['Sold Platform'] || 'Unknown',
        (item) => item['Net Profit'],
    );
    const categoryRoi = roiByGroup(
        soldItems,
        (item) => item.Category || 'Unknown',
    );
    const brandRoi = roiByGroup(soldItems, (item) => item.Brand || 'Unknown');
    const marginDrivers = buildMarginDrivers(soldItems);
    const topCategory = categoryCounts[0];
    const topPlatform = platformCounts[0];

    const recentSales = soldItems
        .filter((item) => Boolean(item['Date Sold']))
        .sort((a, b) => {
            const aDate = parseDate(a['Date Sold']);
            const bDate = parseDate(b['Date Sold']);
            return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 6)
        .map((item) => ({
            id: item.Id,
            title: `${item.Brand || 'Unknown'} ${item.Description || ''}`.trim(),
            subtitle: `${item['Sold Platform'] || 'Unknown platform'} • ${formatRelativeDate(item['Date Sold'])}`,
            price: formatCurrency(item['Sale Price']),
            profit: formatCurrency(item['Net Profit']),
            imageUrls: item['Image URLs'],
            category: item.Category,
        }));

    const monthlySellThrough = buildMonthlySellThrough(rows);
    const weeklySellThrough = buildWeeklySellThrough(rows);
    const monthlyCashFlow = buildMonthlyCashFlow(rows);

    return (
        <Layout>
            <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6">
                <div>
                    <h1 className="text-2xl font-semibold">Analytics</h1>
                    <p className="text-sm text-muted-foreground">
                        Detailed performance breakdowns and trends.
                    </p>
                </div>

                {isLoading && <AnalyticsSkeleton />}

                {isError && (
                    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6 text-sm text-destructive">
                        {(error as Error).message}
                    </div>
                )}

                {!isLoading && !isError && rows.length === 0 && (
                    <Empty>
                        <EmptyHeader>
                            <EmptyMedia variant="icon">
                                <Package className="size-6" />
                            </EmptyMedia>
                            <EmptyTitle>No inventory yet</EmptyTitle>
                            <EmptyDescription>
                                Add items to your inventory to start seeing
                                analytics and performance data.
                            </EmptyDescription>
                        </EmptyHeader>
                        <EmptyContent>
                            <Button asChild>
                                <Link to="/add">
                                    <Plus className="size-4" />
                                    Add your first item
                                </Link>
                            </Button>
                        </EmptyContent>
                    </Empty>
                )}

                {!isLoading && !isError && rows.length > 0 && (
                    <>
                        {/* Sales Performance (date-filtered) */}
                        <SalesCard items={rows} />

                        {/* Inventory Health + P&L Summary */}
                        <div className="grid gap-6 lg:grid-cols-3">
                            <div className="lg:col-span-2 rounded-lg border border-border bg-card p-4 shadow-sm">
                                <h2 className="text-sm font-semibold">
                                    Inventory Health
                                </h2>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    Status breakdown across all {rows.length}{' '}
                                    items
                                </p>
                                <div className="mt-4 flex h-2.5 gap-1 overflow-hidden rounded-full bg-muted/30">
                                    {soldPct > 0 && (
                                        <div
                                            style={{ width: `${soldPct}%` }}
                                            className="rounded-full bg-emerald-500"
                                        />
                                    )}
                                    {listedPct > 0 && (
                                        <div
                                            style={{ width: `${listedPct}%` }}
                                            className="rounded-full bg-sky-500"
                                        />
                                    )}
                                    {otherPct > 0 && (
                                        <div
                                            style={{ width: `${otherPct}%` }}
                                            className="rounded-full bg-muted-foreground/20"
                                        />
                                    )}
                                </div>
                                <div className="mt-5 grid grid-cols-3 gap-4 border-b border-border pb-5">
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            <span className="size-2 shrink-0 rounded-full bg-emerald-500" />
                                            Sold
                                        </div>
                                        <p className="mt-2 text-2xl font-semibold">
                                            {soldItems.length}
                                        </p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            {sellThroughRate.toFixed(0)}%
                                            sell-through
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            <span className="size-2 shrink-0 rounded-full bg-sky-500" />
                                            Listed
                                        </div>
                                        <p className="mt-2 text-2xl font-semibold">
                                            {listedItems.length}
                                        </p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            Active listings
                                        </p>
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                            <span className="size-2 shrink-0 rounded-full bg-muted-foreground/40" />
                                            Total
                                        </div>
                                        <p className="mt-2 text-2xl font-semibold">
                                            {rows.length}
                                        </p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                            All items
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-4">
                                    <div className="flex items-center gap-2 text-xs">
                                        <Clock className="size-3.5 text-muted-foreground" />
                                        <span className="text-muted-foreground">
                                            Avg. days to sell:
                                        </span>
                                        <span className="font-semibold">
                                            {avgDaysToSell
                                                ? `${avgDaysToSell} days`
                                                : '—'}
                                        </span>
                                    </div>
                                    {staleCount > 0 && (
                                        <div className="flex items-center gap-2 text-xs">
                                            <AlertTriangle className="size-3.5 text-amber-500" />
                                            <span className="font-semibold text-amber-600 dark:text-amber-400">
                                                {staleCount} listing
                                                {staleCount !== 1 ? 's' : ''}{' '}
                                                stale 45+ days
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                                <h2 className="text-sm font-semibold">
                                    P&L Summary
                                </h2>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    Based on sold items
                                </p>
                                <div className="mt-4 border-b border-border pb-4">
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                        Net Profit
                                    </p>
                                    <p
                                        className={cn(
                                            'mt-1 text-3xl font-bold',
                                            soldNetProfit >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-rose-600 dark:text-rose-400',
                                        )}
                                    >
                                        {formatCurrency(soldNetProfit)}
                                    </p>
                                    {roi !== null && (
                                        <span
                                            className={cn(
                                                'mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                                roi >= 20
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
                                                    : 'bg-muted text-muted-foreground',
                                            )}
                                        >
                                            {roi.toFixed(0)}% ROI
                                        </span>
                                    )}
                                </div>
                                <div className="mt-4 space-y-2.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">
                                            Total Spend
                                        </span>
                                        <span className="font-medium">
                                            {formatCurrency(totalInventoryCost)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">
                                            Total Sales
                                        </span>
                                        <span className="font-medium">
                                            {formatCurrency(soldSales)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">
                                            Fees Paid
                                        </span>
                                        <span className="font-medium">
                                            {formatCurrency(soldFees)}
                                        </span>
                                    </div>
                                    <div className="h-px bg-border" />
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-muted-foreground">
                                            Cash Position
                                        </span>
                                        <span
                                            className={cn(
                                                'font-semibold',
                                                businessCashPosition >= 0
                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                    : 'text-rose-600 dark:text-rose-400',
                                            )}
                                        >
                                            {formatCurrency(
                                                businessCashPosition,
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Profitability */}
                        <SectionDivider title="Profitability" />
                        <div className="grid gap-6 lg:grid-cols-3">
                            <BarListCard
                                title="ROI by Category"
                                subtitle="Average return on sold items"
                                items={categoryRoi}
                                barClassName="bg-amber-500"
                                valueFormatter={formatPercent}
                                showPercentage={false}
                            />
                            <BarListCard
                                title="ROI by Brand"
                                subtitle="Top brand returns"
                                items={brandRoi.slice(0, 6)}
                                barClassName="bg-amber-500"
                                valueFormatter={formatPercent}
                                showPercentage={false}
                            />
                            <SnapshotCard
                                title="Cash Snapshot"
                                rows={[
                                    {
                                        label: 'Total Spend',
                                        value: formatCurrency(
                                            totalInventoryCost,
                                        ),
                                    },
                                    {
                                        label: 'Total Sales',
                                        value: formatCurrency(soldSales),
                                    },
                                    {
                                        label: 'Sold Fees',
                                        value: formatCurrency(soldFees),
                                    },
                                    {
                                        label: 'Net Profit (Sold)',
                                        value: formatCurrency(soldNetProfit),
                                        valueClassName:
                                            soldNetProfit > 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-rose-600 dark:text-rose-400',
                                    },
                                    {
                                        label: 'Business Cash Position',
                                        value: formatCurrency(
                                            businessCashPosition,
                                        ),
                                        valueClassName:
                                            businessCashPosition >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-rose-600 dark:text-rose-400',
                                    },
                                    {
                                        label: 'ROI',
                                        value:
                                            roi === null
                                                ? '—'
                                                : `${roi.toFixed(0)}%`,
                                        valueClassName:
                                            roi !== null && roi >= 20
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : undefined,
                                    },
                                ]}
                            />
                        </div>

                        {/* Platforms */}
                        <SectionDivider title="Platforms" />
                        <div className="grid gap-6 lg:grid-cols-3">
                            <BarListCard
                                title="Top Selling Platforms"
                                subtitle="Sold items by platform"
                                items={platformCounts}
                                barClassName="bg-emerald-500"
                                className="lg:col-span-2"
                            />
                            <BarListCard
                                title="Profit by Platform"
                                subtitle="Net profit by sales platform"
                                items={platformProfit}
                                barClassName="bg-emerald-500"
                                valueFormatter={formatCurrency}
                            />
                        </div>

                        {/* Trends */}
                        <SectionDivider title="Trends" />
                        <div className="grid gap-6 lg:grid-cols-2">
                            <div className="rounded-lg border border-border bg-card p-4 shadow-sm min-w-0">
                                <Tabs defaultValue="weekly" className="gap-0">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="space-y-1">
                                            <h2 className="text-sm font-semibold">
                                                Sell-through Trend
                                            </h2>
                                            <p className="text-xs text-muted-foreground">
                                                Sold vs listed over time
                                            </p>
                                        </div>
                                        <TabsList className="h-8">
                                            <TabsTrigger
                                                value="weekly"
                                                className="text-xs"
                                            >
                                                Weekly
                                            </TabsTrigger>
                                            <TabsTrigger
                                                value="monthly"
                                                className="text-xs"
                                            >
                                                Monthly
                                            </TabsTrigger>
                                        </TabsList>
                                    </div>
                                    <TabsContent
                                        value="weekly"
                                        className="mt-4"
                                    >
                                        <ChartContainer
                                            config={{
                                                listed: {
                                                    label: 'Listed',
                                                    color: '#94a3b8',
                                                },
                                                sold: {
                                                    label: 'Sold',
                                                    color: '#34d399',
                                                },
                                            }}
                                            className="h-64 w-full"
                                        >
                                            <BarChart data={weeklySellThrough}>
                                                <CartesianGrid
                                                    vertical={false}
                                                    strokeDasharray="3 3"
                                                />
                                                <XAxis
                                                    dataKey="label"
                                                    tickLine={false}
                                                    axisLine={false}
                                                />
                                                <YAxis
                                                    tickLine={false}
                                                    axisLine={false}
                                                    width={32}
                                                />
                                                <ChartTooltip
                                                    cursor={false}
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Bar
                                                    dataKey="listed"
                                                    fill="var(--color-listed)"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar
                                                    dataKey="sold"
                                                    fill="var(--color-sold)"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ChartContainer>
                                    </TabsContent>
                                    <TabsContent
                                        value="monthly"
                                        className="mt-4"
                                    >
                                        <ChartContainer
                                            config={{
                                                listed: {
                                                    label: 'Listed',
                                                    color: '#94a3b8',
                                                },
                                                sold: {
                                                    label: 'Sold',
                                                    color: '#34d399',
                                                },
                                            }}
                                            className="h-64"
                                        >
                                            <BarChart data={monthlySellThrough}>
                                                <CartesianGrid
                                                    vertical={false}
                                                    strokeDasharray="3 3"
                                                />
                                                <XAxis
                                                    dataKey="label"
                                                    tickLine={false}
                                                    axisLine={false}
                                                />
                                                <YAxis
                                                    tickLine={false}
                                                    axisLine={false}
                                                    width={32}
                                                />
                                                <ChartTooltip
                                                    cursor={false}
                                                    content={
                                                        <ChartTooltipContent />
                                                    }
                                                />
                                                <Bar
                                                    dataKey="listed"
                                                    fill="var(--color-listed)"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                                <Bar
                                                    dataKey="sold"
                                                    fill="var(--color-sold)"
                                                    radius={[4, 4, 0, 0]}
                                                />
                                            </BarChart>
                                        </ChartContainer>
                                    </TabsContent>
                                </Tabs>
                            </div>
                            <div className="rounded-lg border border-border bg-card p-4 shadow-sm min-w-0">
                                <div className="space-y-1">
                                    <h2 className="text-sm font-semibold">
                                        Cash Flow
                                    </h2>
                                    <p className="text-xs text-muted-foreground">
                                        Spend vs sales by month
                                    </p>
                                </div>
                                <ChartContainer
                                    config={{
                                        spend: {
                                            label: 'Spend',
                                            color: '#f97316',
                                        },
                                        sales: {
                                            label: 'Sales',
                                            color: '#38bdf8',
                                        },
                                    }}
                                    className="mt-4 h-64 w-full"
                                >
                                    <BarChart data={monthlyCashFlow}>
                                        <CartesianGrid
                                            vertical={false}
                                            strokeDasharray="3 3"
                                        />
                                        <XAxis
                                            dataKey="label"
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            tickLine={false}
                                            axisLine={false}
                                            width={40}
                                        />
                                        <ChartTooltip
                                            cursor={false}
                                            content={
                                                <ChartTooltipContent
                                                    formatter={(value) =>
                                                        formatCurrency(
                                                            typeof value ===
                                                                'number'
                                                                ? value
                                                                : null,
                                                        )
                                                    }
                                                />
                                            }
                                        />
                                        <ChartLegend
                                            content={<ChartLegendContent />}
                                        />
                                        <Bar
                                            dataKey="spend"
                                            fill="var(--color-spend)"
                                            radius={[4, 4, 0, 0]}
                                        />
                                        <Bar
                                            dataKey="sales"
                                            fill="var(--color-sales)"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ChartContainer>
                            </div>
                        </div>

                        {/* Inventory Mix */}
                        <SectionDivider title="Inventory Mix" />
                        <div className="grid gap-6 lg:grid-cols-2">
                            <BarListCard
                                title="Category Breakdown"
                                subtitle="Items by category"
                                items={categoryCounts}
                                barClassName="bg-violet-500"
                            />
                            <BarListCard
                                title="Status Mix"
                                subtitle="How your inventory is tracking"
                                items={statusCounts}
                                barClassName="bg-sky-500"
                            />
                        </div>

                        {/* Recent Activity */}
                        <SectionDivider title="Recent Activity" />
                        <div className="grid gap-6 lg:grid-cols-3">
                            <RecentSalesCard
                                items={recentSales}
                                className="lg:col-span-2"
                            />
                            <div className="space-y-6">
                                <SnapshotCard
                                    title="Margin Drivers"
                                    rows={marginDrivers}
                                />
                                <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
                                    <h2 className="text-sm font-semibold">
                                        Highlights
                                    </h2>
                                    <div className="mt-4 space-y-3 text-sm">
                                        <InsightRow
                                            label="Top Category"
                                            value={topCategory?.label ?? '—'}
                                            helper={
                                                topCategory
                                                    ? `${topCategory.count} items`
                                                    : 'No data yet'
                                            }
                                        />
                                        <InsightRow
                                            label="Top Platform"
                                            value={topPlatform?.label ?? '—'}
                                            helper={
                                                topPlatform
                                                    ? `${topPlatform.count} sales`
                                                    : 'No data yet'
                                            }
                                        />
                                        <InsightRow
                                            label="Sell-through"
                                            value={`${sellThroughRate.toFixed(0)}%`}
                                            helper={`${soldItems.length} sold items`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
}

function SectionDivider({ title }: { title: string }) {
    return (
        <div className="flex items-center gap-3">
            <h2 className="shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {title}
            </h2>
            <div className="h-px flex-1 bg-border" />
        </div>
    );
}

function InsightRow({
    label,
    value,
    helper,
}: {
    label: string;
    value: string;
    helper: string;
}) {
    return (
        <div className="rounded-md border border-border/60 bg-muted/30 p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {label}
            </p>
            <p className="mt-1 text-sm font-semibold">{value}</p>
            <p className="text-xs text-muted-foreground">{helper}</p>
        </div>
    );
}

function AnalyticsSkeleton() {
    return (
        <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-28 w-full" />
                ))}
            </div>
            <Skeleton className="h-36 w-full" />
            <div className="grid gap-6 lg:grid-cols-3">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
            <div className="grid gap-6 lg:grid-cols-3">
                <Skeleton className="h-64 w-full lg:col-span-2" />
                <Skeleton className="h-64 w-full" />
            </div>
        </div>
    );
}
